import React, { useState, useEffect } from "react"
import { MdClose } from "react-icons/md"
import { searchEntities } from "../../../api"
import { useDebounce } from "../../../hooks/useDebounce"
import { SearchEntity } from "../../../api/models"

interface UserSearchBoxProps {
    onSelectEntity?: (entity: SearchEntity) => void
    selectedEntities: SearchEntity[]
    onRemoveEntity: (entityId: string) => void
    includeB2c?: boolean
    lockedEntityIds?: string[]
}

export const UserSearchBox: React.FC<UserSearchBoxProps> = ({
    onSelectEntity,
    selectedEntities,
    onRemoveEntity,
    includeB2c = false,
    lockedEntityIds = []
}) => {
    const [searchQuery, setSearchQuery] = useState("")
    const [results, setResults] = useState<SearchEntity[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const debounced = useDebounce(searchQuery, 300)

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)

    const fetchEntities = async (query: string) => {
        setIsLoading(true)
        try {
            setResults(await searchEntities(query, includeB2c))
        } catch (err) {
            console.error("searchEntities failed:", err)
            setResults([])
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        if (!debounced.trim()) {
            setResults([])
            setIsLoading(false)
            return
        }
        fetchEntities(debounced)
    }, [debounced])

    const handleEntityClick = (entity: SearchEntity) => {
        if (selectedEntities.some(se => se.id === entity.id)) return
        onSelectEntity?.(entity)
        setSearchQuery("")
        setResults([])
    }

    const handleEntityRemove = (entityId: string) => onRemoveEntity(entityId)

    const getSourceBadge = (source?: string) => {
        if (source === "b2c") {
            return <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">B2C</span>
        }
        return null
    }

    return (
        <div className="w-full">
            <div className="user-search-box p-2 border border-jtc-grey rounded-md flex flex-wrap items-center min-h-[48px] focus-within:border-jtc-blue focus-within:ring-jtc-blue focus-within:ring-1">
                {selectedEntities.map(entity => (
                    <div
                        key={entity.id}
                        className="flex items-center bg-gray-300 text-jtc-very-dark-grey rounded-full px-3 py-1 mr-2 mb-1"
                        title={lockedEntityIds.includes(entity.id) ? "Required" : undefined}
                    >
                        <span className="text-sm">{entity.displayName}</span>
                        {getSourceBadge(entity.source)}
                        {!lockedEntityIds.includes(entity.id) && (
                            <button
                                onClick={() => handleEntityRemove(entity.id)}
                                className="ml-2 text-bdo-mid-charcoal hover:text-bdo-charcoal"
                            >
                                <MdClose size={16} />
                            </button>
                        )}
                    </div>
                ))}
                <input
                    type="text"
                    placeholder={includeB2c ? "Search for users or groups..." : "Search for users or groups..."}
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className="flex-grow py-1 px-2 appearance-none border-none outline-none bg-transparent focus:ring-0 focus:border-0"
                />
            </div>
            {isLoading ? (
                <div className="p-3 text-center text-gray-500">Loading...</div>
            ) : (
                results.length > 0 && (
                    <ul className="user-list mt-2 bg-jtc-white border border-jtc-disabled rounded-md shadow-md max-h-60 overflow-y-auto">
                        {results.map(entity => (
                            <li
                                key={entity.id}
                                onClick={() => handleEntityClick(entity)}
                                className="cursor-pointer p-3 border-b border-jtc-disabled last:border-0 hover:bg-jtc-grey"
                            >
                                <div className="flex items-center justify-between">
                                    <div>
                                        <span className="font-medium">{entity.displayName}</span>
                                        <span className="text-jtc-very-dark-grey text-sm ml-2">
                                            {entity.objectType === "user" ? entity.mail : "Group"}
                                        </span>
                                    </div>
                                    {getSourceBadge(entity.source)}
                                </div>
                            </li>
                        ))}
                    </ul>
                )
            )}
        </div>
    )
}